/**
 * React Query hooks for the SOP recordings library — list, detail (signed
 * playback URLs), rename, delete.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

import { fetchApi, fetchApiBlob } from "@/lib/api";

export interface SopDocumentStep {
  screenshot_index: number | null;
  instruction: string;
  detail: string | null;
}

export interface SopDocument {
  title: string;
  summary: string;
  steps: SopDocumentStep[];
  model?: string;
  /** Set when the creator hand-edited the document; Regenerate would discard it. */
  edited_at?: string;
}

export type SopDocumentEdit = Pick<SopDocument, "title" | "summary" | "steps">;

export interface SopRecordingRow {
  id: string;
  title: string | null;
  status: string;
  duration_seconds: number;
  screenshot_count: number;
  has_transcript: boolean;
  has_document: boolean;
  created_by_email: string | null;
  is_mine: boolean;
  created_at: string;
}

export interface SopRecordingDetail {
  id: string;
  title: string | null;
  status: string;
  duration_seconds: number;
  transcript: string | null;
  document: SopDocument | null;
  is_mine: boolean;
  created_at: string;
  audio: { url: string | null; content_type: string };
  screenshots: {
    index: number;
    offset_ms: number;
    click: { x: number; y: number } | null;
    url: string | null;
  }[];
}

export const SOP_RECORDINGS_KEY = ["sop-recordings"];

export function useSopRecordings() {
  return useQuery({
    queryKey: SOP_RECORDINGS_KEY,
    queryFn: (): Promise<{ recordings: SopRecordingRow[] }> => fetchApi("/sop-recorder/recordings"),
    staleTime: 30 * 1000,
  });
}

export function useSopRecording(recordingId: string | undefined) {
  return useQuery({
    queryKey: [...SOP_RECORDINGS_KEY, recordingId],
    queryFn: (): Promise<SopRecordingDetail> => fetchApi(`/sop-recorder/recordings/${recordingId}`),
    enabled: !!recordingId,
  });
}

export function useRenameSopRecording(recordingId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (title: string): Promise<{ id: string; title: string }> =>
      fetchApi(`/sop-recorder/recordings/${recordingId}`, {
        method: "PATCH",
        body: JSON.stringify({ title }),
      }),
    onSuccess: () => {
      // Exact — the list key is a prefix of every detail key, and a
      // prefix-match would refetch the (possibly unmounting) detail query.
      void queryClient.invalidateQueries({ queryKey: SOP_RECORDINGS_KEY, exact: true });
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Rename failed");
    },
  });
}

export function useDeleteSopRecording() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (recordingId: string): Promise<void> =>
      fetchApi(`/sop-recorder/recordings/${recordingId}`, { method: "DELETE" }),
    onSuccess: () => {
      toast.success("Recording deleted.");
      // Exact — avoid refetching the deleted recording's now-404 detail query
      // (which the detail page navigates away from on success).
      void queryClient.invalidateQueries({ queryKey: SOP_RECORDINGS_KEY, exact: true });
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Delete failed");
    },
  });
}

/** Creator-only edits to the generated SOP and the narration transcript. */
export function useEditSopDocument(recordingId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (document: SopDocumentEdit): Promise<{ id: string; document: SopDocument }> =>
      fetchApi(`/sop-recorder/recordings/${recordingId}/document`, {
        method: "PUT",
        body: JSON.stringify(document),
      }),
    onSuccess: ({ document }) => {
      toast.success("SOP saved.");
      // Write the saved document into the cache before the editor closes, so a
      // reopen (or Regenerate's edited check) never sees the pre-save copy.
      queryClient.setQueryData<SopRecordingDetail>([...SOP_RECORDINGS_KEY, recordingId], (old) =>
        old ? { ...old, document } : old,
      );
      void queryClient.invalidateQueries({ queryKey: [...SOP_RECORDINGS_KEY, recordingId] });
      void queryClient.invalidateQueries({ queryKey: SOP_RECORDINGS_KEY, exact: true });
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Couldn't save the SOP");
    },
  });
}

export function useEditSopTranscript(recordingId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (transcript: string): Promise<{ id: string; transcript: string | null }> =>
      fetchApi(`/sop-recorder/recordings/${recordingId}/transcript`, {
        method: "PUT",
        body: JSON.stringify({ transcript }),
      }),
    onSuccess: ({ transcript }) => {
      toast.success("Transcript saved.");
      queryClient.setQueryData<SopRecordingDetail>([...SOP_RECORDINGS_KEY, recordingId], (old) =>
        old ? { ...old, transcript } : old,
      );
      void queryClient.invalidateQueries({ queryKey: [...SOP_RECORDINGS_KEY, recordingId] });
      void queryClient.invalidateQueries({ queryKey: SOP_RECORDINGS_KEY, exact: true });
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "Couldn't save the transcript");
    },
  });
}

export function useGenerateSopDocument(recordingId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (): Promise<{ id: string; status: string; document: SopDocument }> =>
      fetchApi(`/sop-recorder/recordings/${recordingId}/generate`, { method: "POST" }),
    onSuccess: () => {
      toast.success("SOP generated.");
      void queryClient.invalidateQueries({ queryKey: [...SOP_RECORDINGS_KEY, recordingId] });
      void queryClient.invalidateQueries({ queryKey: SOP_RECORDINGS_KEY, exact: true });
    },
    onError: (err) => {
      toast.error(err instanceof Error ? err.message : "SOP generation failed");
    },
  });
}

/** Authenticated PDF download — fetches the bytes, then hands the browser a
 *  one-shot object URL (a plain link can't carry the Authorization header). */
export async function downloadSopPdf(recordingId: string, title: string | null): Promise<void> {
  const blob = await fetchApiBlob(`/sop-recorder/recordings/${recordingId}/pdf`);
  if (!blob) throw new Error("The PDF came back empty — try regenerating the SOP");
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement("a");
    a.href = url;
    a.download = `${(title ?? "sop").replace(/[^\w -]+/g, "").trim() || "sop"}.pdf`;
    a.click();
  } finally {
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
  }
}
